import { MutableRefObject, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import logCustomRouteUpdated from '@alltrails/amplitude/events/logCustomRouteUpdated';
import logCustomRouteStartingPointAdded from '@alltrails/amplitude/events/logCustomRouteStartingPointAdded';
import { Coordinates, LatLng } from '../../../types/Geo';
import { isSinglePointSegment } from '../../../utils/customRoutes/validation';
import { useDispatch, useSelector } from '../../../redux';
import { addActiveSegmentPath, addSegment, selectCustomRouteActiveSegment, setToastMessage } from '../../../redux/customRoute';
import { transformPointsToServerRoutes } from '../../../redux/customRoutesApi';
import useRouting from '../useRouting';
import { validateProximityToPreviousPoint, getPointForHandle } from './validationUtils';

export type PendingHandle = {
  index: number;
  latitude: number;
  longitude: number;
};

const useRouteToPoint = (isSmartRoutingRef: MutableRefObject<boolean>) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { activeSegment, selectedHandleIndex } = useSelector(state => ({
    activeSegment: selectCustomRouteActiveSegment(state),
    selectedHandleIndex: state.customRoute.selectedHandleIndex
  }));
  const [pendingHandle, setPendingHandle] = useState<PendingHandle | undefined>(undefined);
  const routing = useRouting();

  const routeToPoint = useCallback(
    async (coordinates: Coordinates, isPOIAdd?: boolean, isWaypointAdd?: boolean) => {
      const { lng, lat } = coordinates;

      // If smart routing is enabled verify that the first point is routable
      let startPoint: [number, number] = [lat, lng];
      if (isSmartRoutingRef.current && (!activeSegment || activeSegment.paths.length === 0)) {
        const { isSuccess, data } = await routing({
          // Send the first point twice to create a valid route
          routes: transformPointsToServerRoutes([
            [lat, lng],
            [lat, lng]
          ])
        });

        if (!isSuccess) {
          return;
        }

        const serverStartPoint = data?.groupedSegments?.[0]?.pathGroups?.[0]?.paths?.[0]?.points?.[0];
        if (serverStartPoint) {
          startPoint = serverStartPoint;
        }
      }

      if (!activeSegment) {
        dispatch(addSegment({ paths: [{ points: [startPoint], freeDraw: !isSmartRoutingRef.current }] }));
        logCustomRouteStartingPointAdded();
        return;
      }

      if (activeSegment.paths.length === 0) {
        dispatch(addActiveSegmentPath({ points: [startPoint], freeDraw: !isSmartRoutingRef.current }));
        logCustomRouteStartingPointAdded();
        return;
      }

      if (!isSmartRoutingRef.current) {
        const newPoint: [number, number] = [lat, lng];
        const prevPoint = getPointForHandle(activeSegment, selectedHandleIndex);
        if (prevPoint && !validateProximityToPreviousPoint(newPoint, prevPoint)) {
          dispatch(setToastMessage({ iconCode: 'error', message: intl.formatMessage({ defaultMessage: 'Tap or draw closer to your route' }) }));
          return;
        }

        dispatch(addActiveSegmentPath({ points: [[lat, lng]], freeDraw: true }));
        const entryPointLocation = isWaypointAdd ? 'waypoint added' : isPOIAdd ? 'add POI to route' : 'draw';
        const customRouteMode = isWaypointAdd || isPOIAdd ? 'tap' : 'free draw';
        const poiAddLocation = isPOIAdd ? 'custom map screen' : undefined;
        logCustomRouteUpdated({
          entry_point_location: entryPointLocation,
          custom_route_mode: customRouteMode,
          poi_add_location: poiAddLocation
        });
        return;
      }

      if (selectedHandleIndex === undefined) {
        return;
      }

      let pendingHandleSet = false;
      const isStart = selectedHandleIndex === 0;
      const isEnd = selectedHandleIndex === activeSegment.paths.length;
      // The end handle is at the end of the last path rather than at the beginning of a path
      const pathIndex = isEnd ? selectedHandleIndex - 1 : selectedHandleIndex;
      const path = activeSegment.paths[pathIndex];
      const firstPoint = path.points[0];
      const lastPoint = path.points[path.points.length - 1];

      let points: LatLng[] = [];
      if (isStart && !isSinglePointSegment(activeSegment)) {
        // The new path will start at the click location and end at the first point of the existing path
        points = [[lat, lng], firstPoint];
      } else if (isEnd) {
        // The new path will start at the last point of the existing path and end at the click location
        points = [lastPoint, [lat, lng]];
      } else {
        // The new path will connect the first point of the existing path to the click location
        points = [firstPoint, [lat, lng]];
      }

      const { isSuccess, data } = await routing({
        routes: transformPointsToServerRoutes(points),
        onFetch: () => {
          setPendingHandle({ index: selectedHandleIndex, latitude: lat, longitude: lng });
          pendingHandleSet = true;
        }
      });
      const newPath = data?.groupedSegments?.[0]?.pathGroups?.[0]?.paths?.[0];

      if (isSuccess && newPath) {
        dispatch(addActiveSegmentPath(newPath));
        logCustomRouteUpdated({
          entry_point_location: 'add handle',
          custom_route_mode: isSmartRoutingRef.current ? 'smart draw' : 'free draw'
        });
      }

      if (pendingHandleSet) {
        setPendingHandle(undefined);
      }
    },
    [activeSegment, selectedHandleIndex, isSmartRoutingRef, dispatch, routing, intl]
  );

  return { routeToPoint, pendingHandle };
};

export default useRouteToPoint;
